import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { DataGridPro, koKR } from '@mui/x-data-grid-pro';
import {
  Box,
  Button,
  Container,
  CssBaseline,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { AddShoppingCart, Lock, Search, Send, Visibility } from '@mui/icons-material';
import { FormInputAutoComplete, FormInputText } from '../form';
import { gclientOptionLabel, renderGclientOption, toGclientSelectOption } from './gclientSelectOption';
import { CustomNoRowsOverlay } from '../datagrid';
import { AlertDialog, ConfirmDialog } from '../dialog';
import SendMailDialog from '../dialog/SendMailDialog';
import DocumentViewerDrawer from '../common/viewer/DocumentViewerDrawer';
import { createPreviewObjectUrl } from './SubMatFolderFileActions';
import * as subMatFolderActions from '../../store/subMatFolder';
import * as gclientActions from '../../store/gclient';
import * as gsendBasketActions from '../../store/gsendBasket';
import * as securityActions from '../../store/security';
import * as securityRequestActions from '../../store/securityRequest';
import {
  formatSubMatEndDate,
  formatSubMatExpiryStatusText,
  getSubMatExpiryStatus,
  getSubMatExpiryStatusTextColor,
} from './SubMatFolderExpiryStatus';
import '../../utils/Date';

const theme = createTheme();
const DOCUMENT_TYPE = 'SUBMAT_FOLDER';

const STATUS_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'unset', label: '미설정' },
  { value: 'expiring', label: '임박(7일)' },
  { value: 'expired', label: '만료' },
  { value: 'valid', label: '유효' },
  { value: 'none', label: '만료일 없음' },
];

const ROOT_KIND_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'GENERAL', label: '일반문서' },
  { value: 'CERTIFICATION', label: '인증서' },
  { value: 'TEST', label: '성적서' },
  { value: 'ETC', label: '기타' },
];

const toExplorerFolderPath = (folderPath) => {
  if (!folderPath) return '';
  return String(folderPath)
    .split(/\s*(?:›|\/)\s*/)
    .filter(Boolean)
    .join(' › ');
};

const DOCU_SERVICE = '04docu';

const creatorIdOf = (sessionUser) => {
  if (!sessionUser) return '';
  if (sessionUser.sessionUserLoginType === 'USER' && sessionUser.user?.id) {
    return sessionUser.user.id;
  }
  return sessionUser.id || '';
};

const sealGclientOf = (row, gclients) => {
  const full = (gclients || []).find((item) => item.id === row.gclientId);
  return {
    id: row.gclientId,
    name: full?.name || row.gclientName || '',
    signDocumentPath: full?.signDocumentPath || '',
    phrases: full?.phrases || {},
  };
};

const defaultValues = {
  gclient: null,
  rootKind: '',
  expiryStatus: '',
  fileName: '',
};

/**
 * 폴더형 서류보내기.
 * 목록은 만료일 점검과 같은 현재 서류 표이고, 보내기·전송바구니는 레거시 발송을 쓴다.
 */
const SubMatFolderSendManagement = () => {
  const dispatch = useDispatch();
  const sessionUser = useSelector((state) => state.session.sessionUser);
  const gsendBaskets = useSelector((state) => state.gsendBasket.gsendBaskets);
  const isAdmin = sessionUser?.type === 'ADMIN';
  const { control, setValue, getValues } = useForm({ defaultValues });

  const [gclients, setGclients] = useState([]);
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [pageSize, setPageSize] = useState(100);
  const [selectionModel, setSelectionModel] = useState([]);
  const [error, setError] = useState('');
  const [alertInfo, setAlertInfo] = useState({});
  const [openMailDialog, setOpenMailDialog] = useState(false);
  const [mailGsendBaskets, setMailGsendBaskets] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [opened, setOpened] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmInfo, setConfirmInfo] = useState({ title: '', content: null, onConfirm: () => {}, onCancel: () => {} });
  const previewUrlRef = useRef('');

  const selfId = sessionUser?.id || sessionUser?.gclientId;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await gclientActions.selectValidAllDirect();
        if (cancelled) return;
        setGclients(list || []);
        if (selfId) {
          const self = (list || []).find((c) => c.id === selfId);
          if (self) {
            setValue('gclient', toGclientSelectOption(self));
          }
        }
      } catch (_e) {
        if (!cancelled) setGclients([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selfId, setValue]);

  const loadRows = useCallback(async () => {
    setLoaded(false);
    setError('');
    setSelectionModel([]);
    try {
      const gclient = getValues('gclient');
      const rootKind = getValues('rootKind');
      const expiryStatus = getValues('expiryStatus');
      const fileName = (getValues('fileName') || '').trim();
      const targetId = gclient?.id || selfId;
      const viewingOther = Boolean(targetId && selfId && targetId !== selfId && !isAdmin);
      if (viewingOther) {
        const openedList = await securityActions.selectOpenedAboutMeGClientListDirect(DOCU_SERVICE, selfId);
        const allowed = (openedList || []).some((item) => item.id === targetId);
        setOpened(allowed);
        if (!allowed) {
          setRows([]);
          setError('이 거래선은 자재승인서류 열람이 열려 있지 않습니다.');
          if (previewUrlRef.current) {
            window.URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = '';
          }
          setPreviewUrl('');
          setPreviewOpen(false);
          return { denied: true, gclient };
        }
      } else {
        setOpened(true);
      }
      const list = await subMatFolderActions.selectExpiryReviewDirect({
        gclientId: targetId,
        rootKind,
        expiryStatus,
        fileName,
      });
      setRows(Array.isArray(list) ? list : []);
      return { denied: false, gclient };
    } catch (e) {
      setRows([]);
      setError(e?.serverResponse?.message || e.message || '서류 목록을 불러오지 못했습니다.');
      return { denied: false };
    } finally {
      setLoaded(true);
    }
  }, [getValues, isAdmin, selfId]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const selectedRows = useMemo(
    () => rows.filter((row) => selectionModel.includes(row.id)),
    [rows, selectionModel],
  );

  const revokePreviewUrl = useCallback(() => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = '';
    }
    setPreviewUrl('');
  }, []);

  useEffect(() => () => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current);
    }
  }, []);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    revokePreviewUrl();
  }, [revokePreviewUrl]);

  const requestDocuAccess = useCallback((gclient) => {
    const gclientName = gclient?.name || '선택한 거래처';
    const receiverId = gclient?.id;
    setConfirmInfo({
      title: '안내',
      content: (
        <div>
          <p>귀사에 자재승인서류 열람을 허가하지 않은 거래선입니다.</p>
          <p><span style={{ color: '#1976d2' }}>{gclientName}</span>에 열람을 요청하시겠습니까?</p>
        </div>
      ),
      onConfirm: async () => {
        if (!receiverId || !sessionUser?.id) return;
        try {
          const pending = await securityRequestActions.selectGClientsBySecurityRequestStatusByQueryDirect({
            senderId: sessionUser.id,
            receiverIds: [receiverId],
          });
          if (pending?.[0]?.status === '요청') {
            setAlertInfo({
              titleAlert: '안내',
              messageAlert: (
                <span>
                  <span style={{ color: '#1976d2' }}>{gclientName}</span>에 자재승인서류 열람승인이 진행중 상태입니다.
                </span>
              ),
              open: true,
            });
            return;
          }
          await dispatch(securityRequestActions.create({
            id: uuidv4(),
            senderId: sessionUser.id,
            receiverId,
            service: DOCU_SERVICE,
            status: '요청',
          }));
          setAlertInfo({
            titleAlert: '안내',
            messageAlert: '열람 요청을 보냈습니다.',
            open: true,
          });
        } catch (e) {
          setAlertInfo({
            titleAlert: '안내',
            messageAlert: e?.serverResponse?.message || e.message || '열람 요청 중 오류가 발생했습니다.',
            open: true,
          });
        }
      },
      onCancel: () => {},
    });
    setConfirmOpen(true);
  }, [dispatch, sessionUser]);

  const handleChangeGClient = useCallback(async (_e, value) => {
    setValue('gclient', value);
    const result = await loadRows();
    if (result?.denied && value?.id && value.id !== selfId && !isAdmin) {
      requestDocuAccess(value);
    }
  }, [isAdmin, loadRows, requestDocuAccess, selfId, setValue]);

  const handleViewFile = useCallback(async (row) => {
    if (!opened) {
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: '비공개 거래선의 문서는 열람할 수 없습니다.',
        open: true,
      });
      return;
    }
    if (!row?.documentPath) {
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: '파일이 없는 서류는 볼 수 없습니다.',
        open: true,
      });
      return;
    }
    try {
      const url = await createPreviewObjectUrl(row.documentPath);
      revokePreviewUrl();
      previewUrlRef.current = url;
      setPreviewUrl(url);
      setPreviewOpen(true);
    } catch (e) {
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: e?.message || '파일을 열 수 없습니다.',
        open: true,
      });
    }
  }, [opened, revokePreviewUrl]);

  const toMailBasket = useCallback((row) => ({
    id: uuidv4(),
    g04docuId: row.id,
    folderId: row.folderId,
    gclientId: row.gclientId,
    gclient: sealGclientOf(row, gclients),
    documentType: DOCUMENT_TYPE,
    ownerId: sessionUser?.id,
    documentPath: row.documentPath,
    name: {
      type: subMatFolderActions.ROOT_KIND_LABEL[row.rootKind] || '서류',
      name: row.fileName,
      validYN: row.validYN,
    },
  }), [gclients, sessionUser]);

  const openSend = useCallback(() => {
    if (!opened) {
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: '비공개 거래선의 문서는 보낼 수 없습니다.',
        open: true,
      });
      return;
    }
    if (selectedRows.length === 0) {
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: '보낼 서류를 선택해주세요.',
        open: true,
      });
      return;
    }
    const missing = selectedRows.filter((row) => !row.documentPath);
    if (missing.length > 0) {
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: '파일이 없는 서류는 보낼 수 없습니다.',
        open: true,
      });
      return;
    }
    setMailGsendBaskets(selectedRows.map(toMailBasket));
    setOpenMailDialog(true);
  }, [opened, selectedRows, toMailBasket]);

  const putInBasket = useCallback(async () => {
    if (!opened) {
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: '비공개 거래선의 문서이므로 전송바구니에 추가할 수 없습니다.',
        open: true,
      });
      return;
    }
    if (selectedRows.length === 0) {
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: '전송바구니에 담을 서류를 선택해주세요.',
        open: true,
      });
      return;
    }
    const ownerId = sessionUser?.id;
    const existingKeys = new Set(
      (gsendBaskets || []).map((item) => `${item.g04docuId}|${item.gclientId}`),
    );
    const payload = selectedRows
      .filter((row) => !existingKeys.has(`${row.id}|${row.gclientId}`))
      .map((row) => ({
      id: uuidv4(),
      g04docuId: row.id,
      gclientId: row.gclientId,
      documentType: DOCUMENT_TYPE,
      ownerId,
      name: {
        type: subMatFolderActions.ROOT_KIND_LABEL[row.rootKind] || '서류',
        name: row.fileName,
        validYN: row.validYN,
      },
    }));
    const skippedKnown = selectedRows.length - payload.length;
    if (payload.length === 0) {
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: '전송바구니에 이미 추가되어 있습니다.',
        open: true,
      });
      return;
    }
    try {
      const created = await gsendBasketActions.createBulkDirect(payload);
      const sentIds = new Set(payload.map((item) => item.id));
      const createdCount = (Array.isArray(created) ? created : []).filter((row) => sentIds.has(row?.id)).length;
      const creatorId = creatorIdOf(sessionUser);
      if (creatorId) {
        await dispatch(gsendBasketActions.selectAllByCreatorByQuery(creatorId));
      }
      const skipped = skippedKnown + (payload.length - createdCount);
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: skipped > 0
          ? `전송바구니에 ${createdCount}건을 담았습니다. 이미 담긴 ${skipped}건은 건너뛰었습니다.`
          : '전송바구니에 추가되었습니다.',
        open: true,
      });
      setSelectionModel([]);
    } catch (e) {
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: e?.serverResponse?.message || e.message || '전송바구니에 담지 못했습니다.',
        open: true,
      });
    }
  }, [dispatch, gsendBaskets, opened, selectedRows, sessionUser]);

  const columns = useMemo(() => {
    const cols = [
      {
        field: 'gclientName',
        headerName: '거래처',
        width: 160,
      },
    ];
    cols.push(
      {
        field: 'rootKind',
        headerName: '서류종류',
        width: 100,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (params) => subMatFolderActions.ROOT_KIND_LABEL[params.value] || params.value || '',
      },
      {
        field: 'documentPath',
        headerName: '서류',
        width: 72,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const expired = getSubMatExpiryStatus(params.row) === 'expired';
          const fileName = params.row.fileName || '';
          if (!params.row.documentPath) return null;
          if (!opened) {
            return (
              <Tooltip title="비공개 거래선의 문서는 열람할 수 없습니다.">
                <IconButton size="small" aria-label="비공개" sx={{ color: '#BDBDBD' }}>
                  <Lock fontSize="small" />
                </IconButton>
              </Tooltip>
            );
          }
          return (
            <Tooltip title={fileName ? `미리보기 · ${fileName}` : '미리보기'}>
              <IconButton
                size="small"
                aria-label="서류보기"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewFile(params.row);
                }}
                sx={{ color: expired ? '#DB4E2E' : '#2EA2DE' }}
              >
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        field: 'fileName',
        headerName: '파일명',
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'folderPath',
        headerName: '폴더 경로',
        flex: 1.2,
        minWidth: 220,
        valueGetter: (params) => toExplorerFolderPath(params.value),
        renderCell: (params) => {
          const path = params.value || '';
          if (!path) return '';
          const parts = path.split(' › ');
          return (
            <Tooltip title={path} followCursor>
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  minWidth: 0,
                  overflow: 'hidden',
                  fontSize: 'inherit',
                }}
              >
                {parts.map((name, i) => (
                  <Box
                    key={`${i}-${name}`}
                    component="span"
                    sx={{ display: 'inline-flex', alignItems: 'center', minWidth: 0 }}
                  >
                    {i > 0 && (
                      <Box component="span" sx={{ mx: 0.5, color: 'text.secondary', flexShrink: 0 }}>
                        ›
                      </Box>
                    )}
                    <Box
                      component="span"
                      sx={{
                        color: i === parts.length - 1 ? 'text.primary' : 'text.secondary',
                        fontWeight: i === parts.length - 1 ? 500 : 400,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {name}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: 'endDateLabel',
        headerName: '만료일',
        width: 120,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (params) => formatSubMatEndDate(params.row) || '',
      },
      {
        field: 'expiryStatusKey',
        headerName: '상태',
        width: 110,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (params) => formatSubMatExpiryStatusText(params.row) || '',
        renderCell: (params) => {
          const color = getSubMatExpiryStatusTextColor(params.row);
          return (
            <Box component="span" sx={color ? { color } : undefined}>
              {params.value}
            </Box>
          );
        },
      },
    );
    return cols;
  }, [handleViewFile, opened]);

  const gclientOptions = useMemo(
    () => (gclients || []).map((c) => toGclientSelectOption(c)),
    [gclients],
  );

  return (
    <>
      <ThemeProvider theme={theme}>
        <Container component="main" maxWidth="false">
          <CssBaseline />
          <Box
            sx={{
              mt: 3,
              height: 'calc(100vh - 219px)',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 2,
                flexShrink: 0,
              }}
            >
              <Box sx={{ flex: '1 1 280px', minWidth: { xs: '100%', sm: 260 }, maxWidth: { lg: 480 } }}>
                <FormInputAutoComplete
                  name="gclient"
                  control={control}
                  setValue={setValue}
                  label="거래처"
                  noOptionsText="해당 거래처가 없습니다."
                  options={gclientOptions}
                  getOptionLabel={gclientOptionLabel}
                  renderOption={renderGclientOption}
                  isOptionEqualToValue={(a, b) => a?.id === b?.id}
                  onCustomChange={handleChangeGClient}
                />
              </Box>
              <Box sx={{ flex: '1 1 160px', minWidth: { xs: '100%', sm: 150 }, maxWidth: { md: 220 } }}>
                <FormInputText
                  select
                  name="rootKind"
                  control={control}
                  label="서류종류"
                  options={ROOT_KIND_OPTIONS}
                  onChange={(e) => {
                    setValue('rootKind', e.target.value);
                    loadRows();
                  }}
                />
              </Box>
              <Box sx={{ flex: '1 1 160px', minWidth: { xs: '100%', sm: 150 }, maxWidth: { md: 220 } }}>
                <FormInputText
                  select
                  name="expiryStatus"
                  control={control}
                  label="만료 상태"
                  options={STATUS_OPTIONS}
                  onChange={(e) => {
                    setValue('expiryStatus', e.target.value);
                    loadRows();
                  }}
                />
              </Box>
              <Box sx={{ flex: '1 1 200px', minWidth: { xs: '100%', sm: 180 }, maxWidth: { md: 280 } }}>
                <FormInputText
                  name="fileName"
                  control={control}
                  label="파일명으로 검색하세요."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') loadRows();
                  }}
                />
              </Box>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{
                  flex: { xs: '1 1 100%', lg: '0 0 auto' },
                  width: { xs: '100%', sm: 'auto' },
                  ml: { lg: 'auto' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: { sm: 'flex-end' },
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<Send />}
                  disabled={!opened || selectedRows.length === 0}
                  onClick={openSend}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  {`서류 보내기(${selectedRows.length})`}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AddShoppingCart />}
                  disabled={!opened || selectedRows.length === 0}
                  onClick={putInBasket}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  {`전송바구니 담기(${selectedRows.length})`}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Search />}
                  onClick={loadRows}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  조회
                </Button>
              </Stack>
            </Box>

            {error && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>{error}</Typography>
            )}

            <Box sx={{ flex: 1, minHeight: 280, width: '100%', mt: 1.25 }}>
              <DataGridPro
                localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
                sx={{ fontSize: '0.85em', height: '100%' }}
                slots={{
                  noRowsOverlay: CustomNoRowsOverlay,
                  loadingOverlay: LinearProgress,
                }}
                headerHeight={38}
                rowHeight={34}
                loading={!loaded}
                getRowId={(row) => row.id}
                rows={rows}
                columns={columns}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                rowsPerPageOptions={[10, 20, 50, 100]}
                pagination
                checkboxSelection
                disableSelectionOnClick
                selectionModel={selectionModel}
                onSelectionModelChange={(model) => {
                  if (!opened) return;
                  setSelectionModel(model);
                }}
              />
            </Box>
          </Box>
        </Container>
      </ThemeProvider>

      <DocumentViewerDrawer
        key={previewUrl || 'closed'}
        open={previewOpen}
        onClose={closePreview}
        url={previewUrl}
      />
      <SendMailDialog
        open={openMailDialog}
        onClose={() => setOpenMailDialog(false)}
        gsendBaskets={mailGsendBaskets}
        setGSendBaskets={setMailGsendBaskets}
        folderStampItems={mailGsendBaskets}
      />
      <AlertDialog
        alertInfo={alertInfo}
        setAlertInfo={setAlertInfo}
      />
      <ConfirmDialog
        title={confirmInfo.title}
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={confirmInfo.onConfirm}
        onCancel={confirmInfo.onCancel}
      >
        {confirmInfo.content}
      </ConfirmDialog>
    </>
  );
};

export default SubMatFolderSendManagement;
